import { Button, Checkbox, Label } from "@/shared/components/ui";
import UnderwriterModal from "@/shared/components/UnderwriterModal";
import { FormField } from "@/shared/components/FormField";
import { FormHint } from "@/shared/components/FormHint";
import { useModal } from "@/shared/hooks/useModal";
import { cn } from "@/shared/utils";
import { PublicConfig } from "@/shared/PublicConfig";
import { UnderwriterConsentCheckout } from "./UnderwriterConsentCheckout";

const TERMS_URL = `${PublicConfig.PTZ_CA.SPOT_MARKETING_URL}/sample-policy`;

interface CaTermsProps {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    /** Validation error (e.g. Continue clicked without accepting) — mirrors the US Terms consent error UI. */
    error?: string;
}

/**
 * Canada Checkout terms acceptance. The customer must accept before the payment iframe unlocks.
 * "Terms & Conditions" links to the sample policy; "Underwriting Requirements" opens the CA
 * underwriting disclosure modal. No Builder customization slot (unlike the US Terms component).
 */
export function CaTerms({ checked, onCheckedChange, disabled, error }: CaTermsProps) {
    const modal = useModal();

    // Same error treatment as the US Terms: a danger-bordered box around the consent + a hint below.
    const consentErrorClasses = {
        "border border-stroke-danger rounded-md p-4 shadow-md": !!error
    };

    return (
        <>
            <div className={cn("flex flex-col transition-all", consentErrorClasses)}>
                <FormField className="flex-row gap-3">
                    <Checkbox
                        id="ca-terms-consent"
                        data-testid="ca-terms-consent"
                        aria-labelledby="ca-terms-consent-label"
                        checked={checked}
                        error={error}
                        disabled={disabled}
                        onCheckedChange={(value: boolean) => onCheckedChange(value)}
                    />
                    <Label id="ca-terms-consent-label" htmlFor="ca-terms-consent" className="bg-background-transparent text-sm text-content-primary" allowLabelClick>
                        I agree to the policy Terms &amp; Conditions and Underwriting Requirements. View{" "}
                        <a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline">
                            Terms &amp; Conditions
                        </a>{" "}
                        and{" "}
                        <Button
                            type="button"
                            variant="ghost"
                            size="auto"
                            className="text-sm text-content-primary underline hover:bg-background-transparent"
                            onClick={() => modal.openModal(UnderwriterModal, { open: true, title: "Underwriting Requirements", content: <UnderwriterConsentCheckout /> })}
                        >
                            Underwriting Requirements
                        </Button>
                    </Label>
                </FormField>
                {error && (
                    <FormHint variant="error" className="ml-6">
                        {error}
                    </FormHint>
                )}
            </div>
            {modal.render}
        </>
    );
}
